<template>
  <div class="space-y-1 w-full">
    <label
      :for="disabled ? 'disabled' + question.id : question.id"
      class="block text-sm font-semibold leading-6 text-gray-800">
      {{ question.title }}
    </label>

    <template v-if="question.qstList?.qstListRows && question.qstList?.qstListRows.length <= 5">
      <div class="flex flex-wrap gap-3 items-center w-full justify-stretch">
        <div v-for="item in question.qstList?.qstListRows" :key="item.id">
          <input
            :id="disabled ? 'checkbox-disabled' + item.id : 'checkbox-' + item.id"
            type="checkbox"
            :value="item.id"
            :name="disabled ? 'checkbox-disabled' + item.id : 'checkbox-' + item.id"
            :checked="isSelected(item)"
            :disabled="disabled"
            class="hidden peer"
            @change="handleChange(item)" />
          <label
            :for="disabled ? 'checkbox-disabled' + item.id : 'checkbox-' + item.id"
            class="flex justify-center items-baseline px-2 py-3 text-sm font-medium rounded-md text-theme-600 bg-indigo-100/70 min-w-24 text-wrap peer-checked:bg-theme-700 peer-checked:text-white">
            {{ item.description }}
          </label>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="relative mt-2 w-full">
        <div
          class="relative py-3 pr-10 pl-3 w-full text-left bg-white rounded-md ring-1 ring-inset ring-gray-300 shadow-sm cursor-default focus:outline-none focus:ring-1 hover:ring-theme-400 group">
          <div
            v-if="selectedItems.length > 0"
            class="flex flex-wrap gap-2 justify-start items-center">
            <span
              v-for="item in selectedItems"
              :key="disabled ? 'disabled-' + item.id : item.id"
              class="inline-flex gap-x-0.5 items-center px-4 py-2 text-xs font-medium rounded-full bg-theme-100 text-theme-700">
              {{ item.description }}
              <button
                v-if="!disabled"
                type="button"
                @click.stop="removeItem(item)"
                :disabled="disabled"
                class="relative -mr-1 w-3.5 h-3.5 rounded-sm group hover:bg-theme-600/20">
                <svg
                  viewBox="0 0 14 14"
                  class="w-3.5 h-3.5 stroke-theme-800/50 group-hover:stroke-theme-800/75">
                  <path d="M4 4l6 6m0-6l-6 6" />
                </svg>
              </button>
            </span>
          </div>
          <span v-else class="block truncate">Select items {{ selectedItems.length }}</span>
          <button
            class="flex absolute inset-y-0 right-0 items-center pr-2 text-gray-400 group-hover:text-theme-500"
            @click="onShowList"
            :aria-expanded="showList">
            <IconChevronUpDown v-if="!disabled" class="w-5 h-5" />
          </button>
        </div>
        <ul
          v-show="showList"
          class="overflow-auto absolute z-10 py-1 mt-1 w-full max-h-60 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg focus:outline-none"
          tabindex="-1"
          role="listbox">
          <li
            v-for="item in question.qstList?.qstListRows"
            :key="disabled ? 'disabled-' + item.id : item.id"
            class="relative py-2 pr-9 pl-3 text-gray-900 cursor-default select-none"
            role="option"
            @click="handleDropdownChange(item)">
            <span class="block font-normal truncate">{{ item.description }}</span>
            <span
              v-show="isSelected(item)"
              class="flex absolute inset-y-0 right-0 items-center pr-4 text-theme-600">
              <IconCheck class="w-5 h-5" />
            </span>
          </li>
        </ul>
      </div>
    </template>
    <p v-if="v$.model.$error && v$.model.$dirty" class="mt-1 text-xs text-red-600">
      {{ firstError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, defineModel, onMounted, ref, watch } from 'vue';
import IconChevronUpDown from '@/assets/icons/IconChevronUpDown.vue';
import IconCheck from '@/assets/icons/IconCheck.vue';
import type { QstListRow, QstQuestionDetails } from '@/types/Questionnaires';
import { useI18n } from 'vue-i18n';
import { helpers, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

const props = withDefaults(
  defineProps<{
    question: QstQuestionDetails;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);

const data = defineModel<string>({ default: '' });
const selectedItems = ref<QstListRow[]>([]);

function isSelected(item: QstListRow) {
  if (!data.value) return false;
  const ids = data.value.split(',');
  return ids.includes(item.id);
}

const showList = ref(false);

function handleDropdownChange(item: QstListRow) {
  showList.value = false;
  if (props.disabled) return;
  handleChange(item);
}

function handleChange(item: QstListRow) {
  const ids = data.value ? data.value.split(',') : [];

  const index = ids.indexOf(item.id);

  if (index === -1) {
    ids.push(item.id);
    selectedItems.value.push(item);
  } else {
    ids.splice(index, 1);
    selectedItems.value = selectedItems.value.filter((i) => i.id !== item.id);
  }
  data.value = ids.join(',');
}

function removeItem(item: QstListRow) {
  const ids = data.value.split(',');
  const index = ids.indexOf(item.id);

  if (index !== -1) {
    ids.splice(index, 1);
    data.value = ids.join(',');
    selectedItems.value = selectedItems.value.filter((i) => i.id !== item.id);
  }
}

onMounted(() => {
  if (!data.value) return;
  const ids = data.value.split(',');
  selectedItems.value =
    props.question.qstList?.qstListRows?.filter((item) => ids.includes(item.id)) || [];
});

watch(
  () => data.value,
  (newValue) => {
    if (!newValue) return;
    const ids = newValue.split(',');

    selectedItems.value =
      props.question.qstList?.qstListRows?.filter((item) => ids.includes(item.id)) || [];
  }
);

function onShowList() {
  if (props.disabled) return;
  showList.value = !showList.value;
}

const { t: $t } = useI18n({ useScope: 'global' });
const rules = computed(() => {
  const record: Record<string, any> = {};

  if (!props.question.allowNull) {
    record.required = helpers.withMessage($t('messages.errors.requiredField'), required);
  }

  return { model: record };
});

const v$ = useVuelidate(rules, { model: data }, { $lazy: true });

const firstError = computed(() => {
  const errs = v$.value.model.$errors;
  return errs.length ? errs[0].$message : $t('messages.errors.invalidValue');
});
</script>
